import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import logo from "@/assets/Struvent-Logo-2.png";

/**
 * Proactively scrub any corrupted auth tokens from localStorage.
 * A non-ISO-8859-1 character in a persisted JWT will crash fetch()
 * when the Supabase client injects it into an Authorization header.
 */
const scrubCorruptedTokens = () => {
  if (typeof window === "undefined") return;
  const NON_LATIN1 = /[^\u0000-\u00ff]/;
  Object.keys(localStorage).forEach((key) => {
    if (!key.includes("-auth-token")) return;
    try {
      const raw = localStorage.getItem(key) ?? "";
      if (NON_LATIN1.test(raw)) {
        console.warn("[Auth] Removing corrupted token from localStorage key:", key);
        localStorage.removeItem(key);
      }
    } catch {
      localStorage.removeItem(key);
    }
  });
};

const Auth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [mode, setMode] = useState<"login" | "signup" | "forgot">("login");
  const [resetEmail, setResetEmail] = useState("");

  // Scrub corrupted tokens on mount before any auth attempt
  useEffect(() => {
    scrubCorruptedTokens();
  }, []);

  const getErrorMessage = (error: unknown, fallback: string) => {
    if (error instanceof Error && error.message) return error.message;
    if (typeof error === "string" && error.trim().length > 0) return error;
    return fallback;
  };

  const isNetworkError = (error: unknown) => {
    const message = getErrorMessage(error, "");
    return /failed to fetch|networkerror|load failed|network request failed/i.test(message);
  };

  const withRetry = async <T,>(request: () => Promise<T>): Promise<T> => {
    try {
      return await request();
    } catch (error) {
      if (!isNetworkError(error)) throw error;
      await new Promise((resolve) => setTimeout(resolve, 400));
      return request();
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await withRetry(() =>
        supabase.auth.signInWithPassword({ email: email.trim(), password })
      );
      if (error) throw error;

      toast({ title: "Welcome back!", description: "You've successfully logged in." });
      navigate("/");
    } catch (error: unknown) {
      const description = isNetworkError(error)
        ? "Unable to reach authentication. Try 'Email me a sign-in link' below."
        : getErrorMessage(error, "Login failed. Please try again.");

      console.error("Login failed:", { error, online: navigator.onLine, origin: window.location.origin });
      toast({ title: "Login failed", description, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast({ title: "Password too short", description: "Password must be at least 8 characters.", variant: "destructive" });
      return;
    }
    setLoading(true);

    try {
      const { error } = await withRetry(() =>
        supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            emailRedirectTo: window.location.origin,
            data: {
              full_name: fullName.trim(),
              company_name: companyName.trim(),
            },
          },
        })
      );
      if (error) throw error;

      toast({
        title: "Check your email",
        description: "We've sent a confirmation link. Please verify your email to get started.",
      });
      setMode("login");
    } catch (error: unknown) {
      const description = isNetworkError(error)
        ? "Unable to reach authentication. Please check your connection and try again."
        : getErrorMessage(error, "Signup failed. Please try again.");

      console.error("Signup failed:", { error, online: navigator.onLine, origin: window.location.origin });
      toast({ title: "Signup failed", description, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLinkLogin = async () => {
    const targetEmail = email.trim();
    if (!targetEmail) {
      toast({ title: "Email required", description: "Enter your email first.", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      const { error } = await withRetry(() =>
        supabase.auth.signInWithOtp({
          email: targetEmail,
          options: {
            emailRedirectTo: window.location.origin,
          },
        })
      );
      if (error) throw error;

      toast({
        title: "Sign-in link sent",
        description: "Check your email for a one-time sign-in link.",
      });
    } catch (error: unknown) {
      const description = isNetworkError(error)
        ? "Authentication network request failed. Please retry in a private/incognito window."
        : getErrorMessage(error, "Could not send sign-in link.");

      console.error("Magic link failed:", { error, online: navigator.onLine, origin: window.location.origin });
      toast({ title: "Sign-in link failed", description, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await withRetry(() =>
        supabase.auth.resetPasswordForEmail(resetEmail.trim(), {
          redirectTo: `${window.location.origin}/reset-password`,
        })
      );
      if (error) throw error;

      toast({
        title: "Check your email",
        description: "If an account exists for that email, you'll receive a reset link.",
      });
      setMode("login");
    } catch (error: unknown) {
      const description = isNetworkError(error)
        ? "Unable to reach authentication. Please check your connection and try again."
        : getErrorMessage(error, "Request failed. Please try again.");

      console.error("Password reset request failed:", { error, online: navigator.onLine, origin: window.location.origin });
      toast({ title: "Request failed", description, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  if (mode === "forgot") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mb-4 flex justify-center">
              <img src={logo} alt="Struvent" className="h-16 w-auto" />
            </div>
            <CardTitle className="text-2xl">Reset Password</CardTitle>
            <CardDescription>Enter your email to receive a password reset link.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Sending…" : "Send Reset Link"}
              </Button>
            </form>
            <button
              type="button"
              onClick={() => setMode("login")}
              className="mt-4 w-full text-center text-sm text-primary hover:underline"
            >
              Back to login
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mb-4 flex justify-center">
            <img src={logo} alt="Struvent" className="h-16 w-auto" />
          </div>
          <CardTitle className="text-2xl">
            {mode === "signup" ? "Create Your Account" : "Job Manager"}
          </CardTitle>
          <CardDescription>
            {mode === "signup"
              ? "Start managing your construction projects"
              : "Sign in to manage your construction projects"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {mode === "login" ? (
            <>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Logging in..." : "Log In"}
                </Button>
                <Button type="button" variant="outline" className="w-full" onClick={handleMagicLinkLogin} disabled={loading}>
                  {loading ? "Please wait..." : "Email me a sign-in link"}
                </Button>
              </form>
              <button
                type="button"
                onClick={() => setMode("forgot")}
                className="mt-3 w-full text-center text-sm text-primary hover:underline"
              >
                Forgot Password?
              </button>
              <p className="mt-3 text-center text-sm text-muted-foreground">
                Don't have an account?{" "}
                <button
                  type="button"
                  onClick={() => setMode("signup")}
                  className="text-primary hover:underline font-medium"
                >
                  Sign up
                </button>
              </p>
            </>
          ) : (
            <>
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signup-name">Full Name</Label>
                  <Input
                    id="signup-name"
                    type="text"
                    autoComplete="name"
                    placeholder="Jane Smith"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-company">Company Name</Label>
                  <Input
                    id="signup-company"
                    type="text"
                    autoComplete="organization"
                    placeholder="Smith Construction LLC"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    autoComplete="new-password"
                    placeholder="Minimum 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Creating account..." : "Create Account"}
                </Button>
              </form>
              <p className="mt-3 text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className="text-primary hover:underline font-medium"
                >
                  Log in
                </button>
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
